import { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Shield, AlertTriangle, Send, Building2, Lock, Paperclip, X, Upload, User, Mail, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { getLanguageFromCnpjEin } from "../utils/languageUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Relationship types
const relationshipTypes = {
  pt: [
    { value: "colaborador", label: "Colaborador" },
    { value: "terceiro", label: "Terceiro" },
    { value: "cliente", label: "Cliente" },
    { value: "fornecedor", label: "Fornecedor" },
    { value: "comunidade", label: "Comunidade" }
  ],
  en: [
    { value: "colaborador", label: "Employee" },
    { value: "terceiro", label: "Third Party" },
    { value: "cliente", label: "Client" },
    { value: "fornecedor", label: "Supplier" },
    { value: "comunidade", label: "Community" }
  ]
};

export default function EmpresaDenunciaForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [empresa, setEmpresa] = useState(null);
  const [currentLang, setCurrentLang] = useState('pt');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    categoria: "",
    descricao: "",
    data_fato: "",
    local: "",
    pessoas_envolvidas: "",
    // Optional identification fields
    denunciante_nome: "",
    denunciante_email: "",
    denunciante_relacao: "",
    // New mandatory fields
    recorrencia: "", // "unica" or "recorrente"
    houve_testemunhas: "", // "sim" or "nao"
    testemunhas_quem: "",
    // Declarations
    aceita_lgpd: false,
    declara_verdade: false,
    aceita_contato: false,
  });
  const [resultado, setResultado] = useState(null);

  const categorias = [
    { value: "fraude", label: t("whistleblowingForm.categories.fraud", "Fraude Financeira"), desc: t("whistleblowingForm.categories.fraudDesc", "Desvios, apropriação indevida, falsificação"), icon: "💰" },
    { value: "assedio", label: t("whistleblowingForm.categories.harassment", "Assédio"), desc: t("whistleblowingForm.categories.harassmentDesc", "Assédio moral ou sexual"), icon: "⚠️" },
    { value: "seguranca", label: t("whistleblowingForm.categories.safety", "Segurança do Trabalho"), desc: t("whistleblowingForm.categories.safetyDesc", "Riscos, acidentes, condições inseguras"), icon: "🛡️" },
    { value: "corrupcao", label: t("whistleblowingForm.categories.corruption", "Corrupção"), desc: t("whistleblowingForm.categories.corruptionDesc", "Suborno, propina, conflito de interesse"), icon: "🔓" },
    { value: "outro", label: t("whistleblowingForm.categories.other", "Outro"), desc: t("whistleblowingForm.categories.otherDesc", "Outros tipos de irregularidade"), icon: "📝" },
  ];

  const content = {
    pt: {
      title: "Registrar Denúncia",
      subtitle: "Preencha o formulário abaixo. Você pode fazer a denúncia de forma anônima ou identificada.",
      back: "Voltar",
      step1: "Categoria",
      step2: "Detalhes",
      step3: "Identificação (Opcional)",
      step4: "Confirmação",
      selectCategory: "Selecione a categoria da denúncia",
      description: "Descreva o fato",
      descriptionPlaceholder: "Descreva o que aconteceu com o máximo de detalhes possível...",
      date: "Data do fato",
      location: "Local do fato",
      locationPlaceholder: "Ex: Escritório, Filial, Departamento...",
      involved: "Pessoas envolvidas",
      involvedPlaceholder: "Nomes ou cargos das pessoas envolvidas (se souber)...",
      recurrence: "Aconteceu uma vez ou é recorrente?",
      once: "Aconteceu uma única vez",
      recurring: "É recorrente (já aconteceu mais de uma vez)",
      witnesses: "Houve testemunhas?",
      witnessesYes: "Sim, houve testemunhas",
      witnessesNo: "Não houve testemunhas",
      witnessesWho: "Quem são as testemunhas?",
      witnessesWhoPlaceholder: "Informe o nome ou cargo das testemunhas...",
      attachments: "Anexos (opcional)",
      attachmentsDesc: "Adicione documentos, fotos ou vídeos que possam ajudar na investigação",
      addFiles: "Adicionar arquivos",
      optionalId: "Identificação Opcional",
      optionalIdDesc: "Se desejar, você pode se identificar. Esta informação será mantida em sigilo.",
      name: "Seu nome",
      namePlaceholder: "Nome completo (opcional)",
      email: "Seu email",
      emailPlaceholder: "email@exemplo.com (opcional)",
      relationship: "Seu vínculo com a empresa",
      selectRelationship: "Selecione seu vínculo...",
      declarations: "Declarações",
      declareTrue: "Declaro que as informações desta denúncia são verdadeiras e foram prestadas de boa-fé.",
      acceptContact: "Caso tenha me identificado, concordo em ser contactado pelos meios fornecidos para esclarecimentos.",
      acceptLgpd: "Li e concordo com a Política de Privacidade e tratamento de dados conforme a LGPD.",
      next: "Próximo",
      previous: "Anterior",
      submit: "Enviar Denúncia",
      submitting: "Enviando...",
      success: "Denúncia registrada com sucesso!",
      protocolInfo: "Guarde seu protocolo e senha para acompanhar o andamento:",
      protocol: "Protocolo",
      password: "Senha",
      trackReport: "Acompanhar Denúncia",
      newReport: "Nova Denúncia"
    },
    en: {
      title: "Submit Report",
      subtitle: "Fill out the form below. You can submit anonymously or identify yourself.",
      back: "Back",
      step1: "Category",
      step2: "Details",
      step3: "Identification (Optional)",
      step4: "Confirmation",
      selectCategory: "Select the report category",
      description: "Describe the incident",
      descriptionPlaceholder: "Describe what happened in as much detail as possible...",
      date: "Date of incident",
      location: "Location",
      locationPlaceholder: "e.g. Office, Branch, Department...",
      involved: "People involved",
      involvedPlaceholder: "Names or positions of people involved (if known)...",
      recurrence: "Did this happen once or is it recurring?",
      once: "Happened only once",
      recurring: "Recurring (has happened more than once)",
      witnesses: "Were there witnesses?",
      witnessesYes: "Yes, there were witnesses",
      witnessesNo: "No witnesses",
      witnessesWho: "Who are the witnesses?",
      witnessesWhoPlaceholder: "Provide names or positions of witnesses...",
      attachments: "Attachments (optional)",
      attachmentsDesc: "Add documents, photos or videos that may help the investigation",
      addFiles: "Add files",
      optionalId: "Optional Identification",
      optionalIdDesc: "If you wish, you can identify yourself. This information will be kept confidential.",
      name: "Your name",
      namePlaceholder: "Full name (optional)",
      email: "Your email",
      emailPlaceholder: "email@example.com (optional)",
      relationship: "Your relationship with the company",
      selectRelationship: "Select your relationship...",
      declarations: "Declarations",
      declareTrue: "I declare that the information in this report is true and provided in good faith.",
      acceptContact: "If I have identified myself, I agree to be contacted through the provided means for clarification.",
      acceptLgpd: "I have read and agree to the Privacy Policy and data processing in accordance with LGPD.",
      next: "Next",
      previous: "Previous",
      submit: "Submit Report",
      submitting: "Submitting...",
      success: "Report submitted successfully!",
      protocolInfo: "Save your protocol and password to track the progress:",
      protocol: "Protocol",
      password: "Password",
      trackReport: "Track Report",
      newReport: "New Report"
    }
  };

  const c = content[currentLang];

  useEffect(() => {
    fetchEmpresa();
  }, [slug]);

  const fetchEmpresa = async () => {
    try {
      const res = await axios.get(`${API_URL}/empresas/${slug}/check`);
      if (res.data.exists) {
        setEmpresa(res.data);
        // Definir idioma baseado no CNPJ/EIN
        setCurrentLang(getLanguageFromCnpjEin(res.data.cnpj_ein));
      } else {
        navigate("/");
        toast.error("Empresa não encontrada");
      }
    } catch (error) {
      navigate("/");
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'mp3', 'mp4', 'wav'];
      if (!allowed.includes(ext)) {
        toast.error(`Arquivo ${file.name} não permitido`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Arquivo ${file.name} muito grande (máx. 10MB)`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = () => {
    if (step === 1 && !formData.categoria) {
      toast.error(currentLang === 'en' ? 'Please select a category' : 'Por favor, selecione uma categoria');
      return false;
    }
    if (step === 2) {
      if (!formData.descricao || formData.descricao.length < 20) {
        toast.error(currentLang === 'en' ? 'Please provide more details (at least 20 characters)' : 'Por favor, forneça mais detalhes (pelo menos 20 caracteres)');
        return false;
      }
      if (!formData.recorrencia) {
        toast.error(currentLang === 'en' ? 'Please indicate if this is recurring' : 'Por favor, indique se é recorrente');
        return false;
      }
      if (!formData.houve_testemunhas) {
        toast.error(currentLang === 'en' ? 'Please indicate if there were witnesses' : 'Por favor, indique se houve testemunhas');
        return false;
      }
      if (formData.houve_testemunhas === 'sim' && !formData.testemunhas_quem) {
        toast.error(currentLang === 'en' ? 'Please identify the witnesses' : 'Por favor, identifique as testemunhas');
        return false;
      }
    }
    if (step === 3) {
      if (!formData.denunciante_relacao) {
        toast.error(currentLang === 'en' ? 'Please select your relationship with the company' : 'Por favor, selecione seu vínculo com a empresa');
        return false;
      }
    }
    if (step === 4) {
      if (!formData.declara_verdade) {
        toast.error(currentLang === 'en' ? 'You must declare the information is true' : 'Você deve declarar que as informações são verdadeiras');
        return false;
      }
      if (!formData.aceita_lgpd) {
        toast.error(currentLang === 'en' ? 'You must accept the privacy policy' : 'Você deve aceitar a política de privacidade');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep()) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        // Convert boolean to appropriate format
        is_identified: !!(formData.denunciante_nome || formData.denunciante_email)
      };
      
      const response = await axios.post(`${API_URL}/empresas/${slug}/denuncias`, submitData);
      const { protocolo, senha } = response.data;
      
      // Upload files if any
      if (files.length > 0) {
        for (const file of files) {
          const fileFormData = new FormData();
          fileFormData.append("file", file);
          fileFormData.append("protocolo", protocolo);
          fileFormData.append("senha", senha);
          
          try {
            await axios.post(`${API_URL}/empresas/${slug}/denuncias/acompanhar/anexo`, fileFormData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
          } catch (err) {
            console.error("Error uploading attachment:", err);
          }
        }
      }
      
      setResultado({ protocolo, senha });
      setStep(5); // Move to success step
      toast.success(c.success);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar denúncia");
    } finally {
      setLoading(false);
    }
  };

  // Render success state
  if (resultado) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <header className="bg-[#1a1a2e] text-white">
          <div className="max-w-3xl mx-auto px-6 py-4">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">{empresa?.nome}</h1>
                <p className="text-sm text-slate-300">{c.title}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-6 py-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a1a2e] mb-2">{c.success}</h2>
            <p className="text-slate-600 mb-8">{c.protocolInfo}</p>
            
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{c.protocol}</p>
                  <p className="text-xl font-mono font-bold text-[#1a1a2e]">{resultado.protocolo}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">{c.password}</p>
                  <p className="text-xl font-mono font-bold text-[#1a1a2e]">{resultado.senha}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/${slug}/acompanhar?protocolo=${resultado.protocolo}&senha=${resultado.senha}`}>
                <Button className="bg-[#1a1a2e]">{c.trackReport}</Button>
              </Link>
              <Link to={`/${slug}`}>
                <Button variant="outline">{c.newReport}</Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{empresa?.nome ? `Denúncia Anônima - ${empresa.nome}` : 'Canal de Denúncias Anônimas'}</title>
        <meta name="description" content={`Faça uma denúncia anônima e segura para ${empresa?.nome || 'a empresa'}. Proteja sua identidade e reporte irregularidades com total confidencialidade.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#1a1a2e] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to={`/${slug}`} className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            {c.back}
          </Link>
          <div className="flex items-center gap-3">
            {empresa?.logo_url ? (
              <img 
                src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)} 
                alt="" 
                className="h-8" 
              />
            ) : (
              <Shield className="w-8 h-8" />
            )}
            <div>
              <h1 className="text-xl font-bold">{empresa?.nome || c.title}</h1>
              <p className="text-sm text-slate-300">{c.subtitle}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? "bg-[#1a1a2e] text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {s}
              </div>
              {s < 4 && <div className={`w-12 h-1 ${step > s ? "bg-[#1a1a2e]" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Category */}
          {step === 1 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <h2 className="text-xl font-bold text-[#1a1a2e] mb-2">{c.step1}</h2>
              <p className="text-slate-500 mb-6">{c.selectCategory}</p>
              
              <div className="grid gap-3">
                {categorias.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleChange("categoria", cat.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.categoria === cat.value
                        ? "border-[#1a1a2e] bg-[#1a1a2e]/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{cat.icon}</span>
                      <div>
                        <p className="font-semibold text-[#1a1a2e]">{cat.label}</p>
                        <p className="text-sm text-slate-500">{cat.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-8 flex justify-end">
                <Button 
                  type="button"
                  onClick={handleNext}
                  disabled={!formData.categoria}
                  className="bg-[#1a1a2e]"
                >
                  {c.next}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <h2 className="text-xl font-bold text-[#1a1a2e] mb-2">{c.step2}</h2>
              <p className="text-slate-500 mb-6">{c.description}</p>

              <div className="space-y-6">
                <div>
                  <Label>{c.description} *</Label>
                  <Textarea
                    value={formData.descricao}
                    onChange={(e) => handleChange("descricao", e.target.value)}
                    placeholder={c.descriptionPlaceholder}
                    className="mt-1 min-h-[150px]"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>{c.date}</Label>
                    <Input
                      type="date"
                      value={formData.data_fato}
                      onChange={(e) => handleChange("data_fato", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{c.location}</Label>
                    <Input
                      value={formData.local}
                      onChange={(e) => handleChange("local", e.target.value)}
                      placeholder={c.locationPlaceholder}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>{c.involved}</Label>
                  <Textarea
                    value={formData.pessoas_envolvidas}
                    onChange={(e) => handleChange("pessoas_envolvidas", e.target.value)}
                    placeholder={c.involvedPlaceholder}
                    className="mt-1"
                  />
                </div>

                {/* Recurrence */}
                <div>
                  <Label className="mb-3 block">{c.recurrence} *</Label>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => handleChange("recorrencia", "unica")}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        formData.recorrencia === "unica"
                          ? "border-[#1a1a2e] bg-[#1a1a2e]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {c.once}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange("recorrencia", "recorrente")}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        formData.recorrencia === "recorrente"
                          ? "border-[#1a1a2e] bg-[#1a1a2e]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {c.recurring}
                    </button>
                  </div>
                </div>

                {/* Witnesses */}
                <div>
                  <Label className="mb-3 block">{c.witnesses} *</Label>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => handleChange("houve_testemunhas", "sim")}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        formData.houve_testemunhas === "sim"
                          ? "border-[#1a1a2e] bg-[#1a1a2e]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {c.witnessesYes}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange("houve_testemunhas", "nao")}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        formData.houve_testemunhas === "nao"
                          ? "border-[#1a1a2e] bg-[#1a1a2e]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {c.witnessesNo}
                    </button>
                  </div>
                  
                  {formData.houve_testemunhas === "sim" && (
                    <div className="mt-4">
                      <Label>{c.witnessesWho} *</Label>
                      <Textarea
                        value={formData.testemunhas_quem}
                        onChange={(e) => handleChange("testemunhas_quem", e.target.value)}
                        placeholder={c.witnessesWhoPlaceholder}
                        className="mt-1"
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Attachments */}
                <div>
                  <Label>{c.attachments}</Label>
                  <p className="text-sm text-slate-500 mb-2">{c.attachmentsDesc}</p>
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.mp3,.mp4,.wav"
                    />
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Paperclip className="w-4 h-4 mr-2" />
                        {c.addFiles}
                      </Button>
                    </div>
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                            <span className="text-sm truncate">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  {c.previous}
                </Button>
                <Button type="button" onClick={handleNext} className="bg-[#1a1a2e]">
                  {c.next}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Identification and Relationship */}
          {step === 3 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <h2 className="text-xl font-bold text-[#1a1a2e] mb-2">{c.step3}</h2>
              <p className="text-slate-500 mb-6">{c.optionalIdDesc}</p>

              <div className="space-y-6">
                {/* Mandatory: Relationship with Company */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-2">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      {currentLang === 'en' 
                        ? "Please indicate your relationship with the company. This is required for statistical purposes."
                        : "Por favor, indique seu vínculo com a empresa. Esta informação é obrigatória para fins estatísticos."}
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {c.relationship} *
                  </Label>
                  <select
                    value={formData.denunciante_relacao}
                    onChange={(e) => handleChange("denunciante_relacao", e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">{c.selectRelationship}</option>
                    {relationshipTypes[currentLang].map((rel) => (
                      <option key={rel.value} value={rel.value}>{rel.label}</option>
                    ))}
                  </select>
                </div>

                {/* Separator */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-2">{c.optionalId}</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
                      <p className="text-sm text-blue-800">
                        {currentLang === 'en' 
                          ? "If you wish, you can identify yourself. This information is optional and will be kept strictly confidential."
                          : "Se desejar, você pode se identificar. Esta informação é opcional e será mantida em estrito sigilo."}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {c.name}
                  </Label>
                  <Input
                    value={formData.denunciante_nome}
                    onChange={(e) => handleChange("denunciante_nome", e.target.value)}
                    placeholder={c.namePlaceholder}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {c.email}
                  </Label>
                  <Input
                    type="email"
                    value={formData.denunciante_email}
                    onChange={(e) => handleChange("denunciante_email", e.target.value)}
                    placeholder={c.emailPlaceholder}
                    className="mt-1"
                  />
                </div>

                {/* Contact consent - only shows if email is provided */}
                {formData.denunciante_email && (
                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                    <Checkbox
                      id="aceita_contato_step3"
                      checked={formData.aceita_contato}
                      onCheckedChange={(checked) => handleChange("aceita_contato", checked)}
                      className="mt-1"
                    />
                    <Label htmlFor="aceita_contato_step3" className="cursor-pointer text-sm leading-relaxed text-green-800">
                      {c.acceptContact}
                    </Label>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(2)}>
                  {c.previous}
                </Button>
                <Button type="button" onClick={handleNext} className="bg-[#1a1a2e]">
                  {c.next}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Declarations */}
          {step === 4 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <h2 className="text-xl font-bold text-[#1a1a2e] mb-2">{c.step4}</h2>
              <p className="text-slate-500 mb-6">{c.declarations}</p>

              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <Checkbox
                    id="declara_verdade"
                    checked={formData.declara_verdade}
                    onCheckedChange={(checked) => handleChange("declara_verdade", checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="declara_verdade" className="cursor-pointer text-sm leading-relaxed text-amber-900">
                    {c.declareTrue} *
                  </Label>
                </div>

                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                  <Checkbox
                    id="aceita_lgpd"
                    checked={formData.aceita_lgpd}
                    onCheckedChange={(checked) => handleChange("aceita_lgpd", checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="aceita_lgpd" className="cursor-pointer text-sm leading-relaxed">
                    {c.acceptLgpd} *
                  </Label>
                </div>

                {/* Summary of identification */}
                {(formData.denunciante_nome || formData.denunciante_email) && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
                    <p className="text-sm text-green-800 font-medium mb-2">
                      {currentLang === 'en' ? 'You chose to identify yourself:' : 'Você optou por se identificar:'}
                    </p>
                    <ul className="text-sm text-green-700 space-y-1">
                      {formData.denunciante_nome && <li>• {c.name}: {formData.denunciante_nome}</li>}
                      {formData.denunciante_email && <li>• {c.email}: {formData.denunciante_email}</li>}
                      {formData.aceita_contato && (
                        <li className="text-green-600">✓ {currentLang === 'en' ? 'Agreed to be contacted by the auditor' : 'Concordou em ser contactado pelo auditor'}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(3)}>
                  {c.previous}
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || !formData.declara_verdade || !formData.aceita_lgpd}
                  className="bg-[#1a1a2e]"
                >
                  {loading ? c.submitting : c.submit}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </form>
      </main>
    </div>
  );
}
